import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { PrincipalContextProvider } from './context/PrincipalContext';
import { setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import { CadenceThemeProvider } from './theme/ThemeContext';
import AppLayout from './components/AppLayout';
import DashboardPage from './pages/DashboardPage';
import DashboardBriefingPage from './pages/DashboardBriefingPage';
import UnifiedPlanPage from './pages/UnifiedPlanPage';
import EaLandingPage from './pages/EaLandingPage';
import { useDashboardVariant } from './hooks/useDashboardVariant';
import WorkItemsPage from './pages/WorkItemsPage';
import CalendarPage from './pages/CalendarPage';
import StrategyPage from './pages/StrategyPage';
import ImagesPage from './pages/ImagesPage';
import ContextPage from './pages/ContextPage';
import PreferencesPage from './pages/PreferencesPage';
import FocusTimePage from './pages/FocusTimePage';
import ReportsPage from './pages/ReportsPage';
import { TranscriptsPage } from './pages/TranscriptsPage';
import EAWorkspacePage from './pages/EAWorkspacePage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import TenantBillingPage from './pages/TenantBillingPage';
import TenantOrganisationPage from './pages/TenantOrganisationPage';
import SignupPage from './pages/SignupPage';
import OnboardingSuccessPage from './pages/OnboardingSuccessPage';
import OAuthCallback from './auth/OAuthCallback';
import MagicLinkCallback from './auth/MagicLinkCallback';
import MfaSetupPage from './pages/MfaSetupPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PrincipalProfilePage from './pages/PrincipalProfilePage';
import WebhooksPage from './pages/WebhooksPage';
import ReflectionsPage from './pages/ReflectionsPage';
import FeatureRequestPage from './pages/FeatureRequestPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import IdeasPage from './pages/IdeasPage';
import ConnectionsPage from './pages/ConnectionsPage';
import DecisionsPage from './pages/DecisionsPage';
import AuditLogPage from './pages/AuditLogPage';
import HorizonPage from './pages/HorizonPage';
import DelegationPage from './pages/DelegationPage';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';

// Admin / platform / prompt-management pages are lazy-loaded. The executive
// majority never opens them, so shipping code they will not run is dead
// weight on first paint. Wrapping the Routes in <Suspense> means a quick
// spinner the first time a platform admin hits one of these surfaces.
const PromptManagementPage = lazy(() => import('./pages/PromptManagementPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PlatformOverviewPage = lazy(() => import('./pages/PlatformOverviewPage'));
const PlatformTenantsPage = lazy(() => import('./pages/PlatformTenantsPage'));
const PlatformProductsPage = lazy(() => import('./pages/PlatformProductsPage'));
const PlatformEmailPage = lazy(() => import('./pages/PlatformEmailPage'));
const PlatformSecurityPage = lazy(() => import('./pages/PlatformSecurityPage'));
const PlatformBillingPage = lazy(() => import('./pages/PlatformBillingPage'));

const publicDarkTheme = createTheme({ palette: { mode: 'dark' } });

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, ensureValidToken } = useAuth();

  useEffect(() => {
    setTokenGetter(ensureValidToken);
  }, [ensureValidToken]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
}

// Route "/" based on user role and the dashboard variant preference.
//  - PlatformAdmin lands on the platform overview (unchanged).
//  - ExecutiveAssistant lands on the cross-principal EA landing (Phase 2 task
//    2.4). Their own dashboard was empty anyway; the landing shows the four
//    cross-principal alert cards plus a link into the full EA workspace.
//  - Everyone else (Executive, Admin) keeps the dashboard variant they chose.
function RoleBasedHome() {
  const { user } = useAuth();
  const { variant } = useDashboardVariant();
  if (user?.role === 'PlatformAdmin') {
    return <PlatformOverviewPage />;
  }
  if (user?.role === 'ExecutiveAssistant') {
    return <EaLandingPage />;
  }
  return variant === 'briefing' ? <DashboardBriefingPage /> : <DashboardPage />;
}

function LazyRouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
      <CircularProgress size={24} />
    </Box>
  );
}

function ProtectedApp() {
  return (
    <AuthProvider>
      <PrincipalContextProvider>
      <AuthGate>
        <Suspense fallback={<LazyRouteFallback />}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<RoleBasedHome />} />
            <Route path="/mfa-setup" element={<MfaSetupPage />} />
            <Route path="/getting-started" element={<OnboardingWizardPage />} />
            <Route path="/plan" element={<UnifiedPlanPage />} />
            <Route path="/focus" element={<FocusTimePage />} />
            <Route path="/work-items" element={<WorkItemsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/strategy" element={<StrategyPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/ideas" element={<IdeasPage />} />
            <Route path="/images" element={<ImagesPage />} />
            <Route path="/transcripts" element={<TranscriptsPage />} />
            <Route path="/context" element={<ContextPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/horizon" element={<HorizonPage />} />
            <Route path="/delegation" element={<DelegationPage />} />
            <Route path="/reflections" element={<ReflectionsPage />} />
            <Route path="/ea-workspace" element={<EAWorkspacePage />} />
            <Route path="/principal-profile" element={<PrincipalProfilePage />} />
            <Route path="/principal-profile/:execId" element={<PrincipalProfilePage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/decisions" element={<DecisionsPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/feature-requests" element={<FeatureRequestPage />} />
            <Route path="/prompt-management" element={<PromptManagementPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/billing" element={<TenantBillingPage />} />
            <Route path="/organisation" element={<TenantOrganisationPage />} />
            <Route path="/platform" element={<PlatformOverviewPage />} />
            <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
            <Route path="/platform/products" element={<PlatformProductsPage />} />
            <Route path="/platform/email" element={<PlatformEmailPage />} />
            <Route path="/platform/security" element={<PlatformSecurityPage />} />
            <Route path="/platform/billing" element={<PlatformBillingPage />} />
          </Route>
          {/* OAuth authorize: rendered without AppLayout chrome so the consent
              screen is the only thing the user sees. AuthGate above still gates
              on a valid Cadence session — login round-trips back here on success. */}
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          {/* Route aliases for common URL patterns */}
          <Route path="/strategic-drivers" element={<Navigate to="/strategy" replace />} />
          <Route path="/focus-time" element={<Navigate to="/focus" replace />} />
          <Route path="/settings" element={<Navigate to="/preferences" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          {/* Catch /login and any unknown routes — redirect to dashboard */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthGate>
      </PrincipalContextProvider>
    </AuthProvider>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function AppRouter() {
  const pathname = window.location.pathname;

  // Public routes — render WITHOUT auth
  if (pathname === '/signup' || pathname.startsWith('/onboarding') || pathname === '/auth/callback' || pathname === '/auth/magic-link' || pathname === '/reset-password' || pathname === '/forgot-password') {
    return (
      <ThemeProvider theme={publicDarkTheme}>
        <Routes>
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding/success" element={<OnboardingSuccessPage />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Routes>
      </ThemeProvider>
    );
  }

  // Everything else — protected
  return <ProtectedApp />;
}

function App() {
  return (
    <CadenceThemeProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </CadenceThemeProvider>
  );
}

export default App;
